import { codeFont, numFont, plateFont } from '../../../utils/fonts';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DownloadIcon from '@mui/icons-material/Download';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HistoryIcon from '@mui/icons-material/History';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import PublicHeader from './PublicHeader';
import PublicFooter from './PublicFooter';
import WorkshopContactCard from './WorkshopContactCard';
import { theme } from './theme';

// Separate axios instance: no auth credentials, the token in the URL is the credential.
const publicAxios = axios.create({
  withCredentials: false,
  headers: { 'Content-Type': 'application/json' },
});

const formatPrice = (value) =>
  new Intl.NumberFormat('fi-FI', { style: 'currency', currency: 'EUR' }).format(parseFloat(value) || 0);

const formatDate = (value) => {
  if (!value) return '';
  return new Date(value).toLocaleDateString('fi-FI', { day: 'numeric', month: 'numeric', year: 'numeric' });
};

// Bookings and note dates are shown in workshop time, whatever the customer's browser is
// set to, so the page agrees with the PDF.
const formatWorkshopDate = (value) =>
  new Date(value).toLocaleDateString('fi-FI', {
    timeZone: 'Europe/Helsinki',
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  });
const formatWorkshopTime = (value) =>
  new Date(value).toLocaleTimeString('fi-FI', { timeZone: 'Europe/Helsinki', hour: '2-digit', minute: '2-digit' });

// A slot over two days names both dates: "5.11. klo 9-16" would read as a one-day job.
const bookingText = (slot, t) => {
  const startDate = formatWorkshopDate(slot.start);
  const endDate = formatWorkshopDate(slot.end);
  const start = formatWorkshopTime(slot.start);
  const end = formatWorkshopTime(slot.end);
  return startDate === endDate
    ? t('quoteSnapshot.booking', { date: startDate, start, end })
    : t('quoteSnapshot.bookingMultiDay', { startDate, start, endDate, end });
};

// Development StrictMode runs the load effect twice. Share the in-flight request so the
// public quote API, throttled per IP, sees one call per page load.
const inflightQuoteRequests = new Map();
const loadQuote = (token) => {
  if (!inflightQuoteRequests.has(token)) {
    const request = publicAxios
      .get(`/api/public/quote/${token}/`)
      .finally(() => inflightQuoteRequests.delete(token));
    inflightQuoteRequests.set(token, request);
  }
  return inflightQuoteRequests.get(token);
};

const formatDateTime = (value) => {
  if (!value) return '';
  return new Date(value).toLocaleString('fi-FI', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatQty = (value) => {
  const n = parseFloat(value) || 0;
  return Number.isInteger(n) ? String(n) : n.toLocaleString('fi-FI', { maximumFractionDigits: 2 });
};

const cardSx = {
  backgroundColor: theme.colors.surface,
  borderRadius: theme.radius.xl,
  padding: '20px',
  boxShadow: theme.shadows.md,
  marginBottom: '20px',
};

const sectionTitleSx = {
  fontSize: '16px',
  fontWeight: 700,
  margin: '0 0 12px 0',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

function QuoteLinesCard({ quote, t }) {
  const unitPriceLabel = quote.unit_price_incl_vat ? t('quoteSnapshot.unitPriceInclVat') : t('quoteSnapshot.unitPriceExclVat');
  const hasMargin = quote.lines.some((line) => line.is_margin_vat_scheme);
  // Snapshots taken before the net-row modes existed have no flag at all, so anything
  // but an explicit false keeps the gross row total the PDF printed. Margin-VAT rows
  // are always gross: their net figure is an internal accounting value.
  const netRowTotals = quote.line_totals_incl_vat === false;
  const rowTotal = (line) => (
    netRowTotals && !line.is_margin_vat_scheme ? line.line_total_excl_vat : line.line_total_incl_vat
  );

  // Mirrors the PDF the customer was sent: when the snapshot was taken with the
  // organization's grouping on, a heading opens each kind and a subtotal of the printed
  // row totals closes it. Snapshot lines are stored kind by kind (labour then parts, or
  // the reverse), so a change of kind is a group boundary. Summed in whole cents so
  // float drift cannot show up in the last digit.
  const rows = [];
  if (quote.group_subtotals === true) {
    let currentKind = null;
    let cents = 0;
    quote.lines.forEach((line) => {
      if (line.kind !== currentKind) {
        if (currentKind !== null) rows.push({ type: 'subtotal', kind: currentKind, amount: cents / 100 });
        currentKind = line.kind;
        cents = 0;
        rows.push({ type: 'header', kind: currentKind });
      }
      cents += Math.round((parseFloat(rowTotal(line)) || 0) * 100);
      rows.push({ type: 'line', line });
    });
    if (currentKind !== null) rows.push({ type: 'subtotal', kind: currentKind, amount: cents / 100 });
  } else {
    quote.lines.forEach((line) => rows.push({ type: 'line', line }));
  }
  const groupLabel = (kind) => (kind === 'job' ? t('quoteSnapshot.groupJobs') : t('quoteSnapshot.groupParts'));
  const subtotalLabel = (kind) => (kind === 'job' ? t('quoteSnapshot.jobsTotal') : t('quoteSnapshot.partsTotal'));

  return (
    <Paper elevation={0} sx={cardSx} data-testid="public-quote-lines">
      <Typography component="h3" sx={sectionTitleSx}>
        {t('quoteSnapshot.lines')}
      </Typography>
      {netRowTotals && (
        <Typography sx={{ fontSize: '12px', color: theme.colors.textMuted, mb: 1 }}>
          {t('quoteSnapshot.rowPricesExclVat')}
        </Typography>
      )}
      {rows.map((row, index) => {
        const last = index === rows.length - 1;
        if (row.type === 'header') {
          return (
            <Typography
              key={`header-${row.kind}`}
              data-testid={`public-quote-group-header-${row.kind}`}
              sx={{
                fontSize: '12px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.3px',
                color: theme.colors.textMuted,
                pt: index === 0 ? 0 : 2,
                pb: 0.5,
              }}
            >
              {groupLabel(row.kind)}
            </Typography>
          );
        }
        if (row.type === 'subtotal') {
          return (
            <Box
              key={`subtotal-${row.kind}`}
              data-testid={`public-quote-group-subtotal-${row.kind}`}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 2,
                py: 1,
                borderBottom: last ? 'none' : `1px solid ${theme.colors.borderLight}`,
              }}
            >
              <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>{subtotalLabel(row.kind)}</Typography>
              <Typography sx={{ fontSize: '14px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                {formatPrice(row.amount)}
              </Typography>
            </Box>
          );
        }
        const { line } = row;
        return (
        <Box
          key={index}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            py: 1.25,
            borderBottom: last ? 'none' : `1px solid ${theme.colors.borderLight}`,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, wordBreak: 'break-word' }}>
              {line.name}
              {line.is_margin_vat_scheme ? ' *' : ''}
            </Typography>
            {line.description && (
              <Typography sx={{ fontSize: '13px', color: theme.colors.textMuted, whiteSpace: 'pre-wrap' }}>
                {line.description}
              </Typography>
            )}
            {line.code && (
              <Typography
                data-testid="public-quote-line-code"
                sx={{ fontSize: '12px', color: theme.colors.textMuted, wordBreak: 'break-all', ...codeFont }}
              >
                {line.code}
              </Typography>
            )}
            <Typography sx={{ fontSize: '12px', color: theme.colors.textLight, mt: 0.25 }}>
              {line.kind === 'job' ? t('quoteSnapshot.kindJob') : t('quoteSnapshot.kindPart')}
              {' · '}
              {formatQty(line.quantity)} {line.unit}
              {quote.show_unit_price && (
                <>
                  {' × '}
                  {formatPrice(line.display_unit_price)}
                  {' '}
                  <Box component="span" sx={{ whiteSpace: 'nowrap' }}>({unitPriceLabel})</Box>
                </>
              )}
              {parseFloat(line.discount_percent) > 0 && (
                <>
                  {' · '}
                  {t('quoteSnapshot.discount', { percent: formatQty(line.discount_percent) })}
                </>
              )}
              {quote.show_line_vat && !line.is_margin_vat_scheme && (
                <>
                  {' · '}
                  {t('quoteSnapshot.vat', { rate: formatQty(line.vat_rate) })}
                </>
              )}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, whiteSpace: 'nowrap' }}>
            {formatPrice(rowTotal(line))}
          </Typography>
        </Box>
        );
      })}
      {hasMargin && (
        <Typography sx={{ fontSize: '12px', color: theme.colors.textMuted, mt: 1.5 }}>
          * {t('quoteSnapshot.marginVatNotice')}
        </Typography>
      )}
    </Paper>
  );
}

function QuoteTotalsCard({ quote, t }) {
  const discount = parseFloat(quote.order_discount_amount) || 0;
  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: theme.colors.primary,
        borderRadius: theme.radius.xl,
        padding: '20px',
        color: 'white',
        marginBottom: '20px',
      }}
      data-testid="public-quote-totals"
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75, fontSize: '14px', opacity: 0.85 }}>
        <span>{t('quoteSnapshot.subtotalExclVat')}</span>
        <span>{formatPrice(quote.subtotal_excl_vat)}</span>
      </Box>
      {discount > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75, fontSize: '14px', opacity: 0.85 }}>
          <span>{t('quoteSnapshot.orderDiscount')}</span>
          <span>-{formatPrice(discount)}</span>
        </Box>
      )}
      {(quote.vat_summary || []).map((group, index) => (
        <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75, fontSize: '14px', opacity: 0.85 }}>
          <span>
            {t('quoteSnapshot.vat', { rate: formatQty(group.rate) })}
            {group.is_margin_scheme ? ' *' : ''}
          </span>
          <span>{formatPrice(group.is_margin_scheme ? 0 : group.vat)}</span>
        </Box>
      ))}
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', my: 1.5 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>{t('quoteSnapshot.total')}</Typography>
        <Typography sx={{ fontSize: '24px', fontWeight: 700 }} data-testid="public-quote-total">
          {formatPrice(quote.total_incl_vat)}
        </Typography>
      </Box>
    </Paper>
  );
}

function ResultCard({ accepted, title, description, respondedAt, note, t, testId }) {
  return (
    <Card
      data-testid={testId || 'public-quote-result'}
      sx={{ mb: '20px', borderRadius: theme.radius.xl, border: '2px solid', borderColor: accepted ? 'success.main' : 'error.main' }}
    >
      <CardContent sx={{ textAlign: 'center', py: 4 }}>
        {accepted ? (
          <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
        ) : (
          <CancelIcon sx={{ fontSize: 48, color: 'error.main', mb: 1 }} />
        )}
        <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
        {description && <Typography color="text.secondary">{description}</Typography>}
        {respondedAt && (
          <Typography color="text.secondary" sx={{ mt: 0.5, fontSize: '13px' }}>{formatDateTime(respondedAt)}</Typography>
        )}
        {note && (
          <Typography sx={{ mt: 1.5, fontStyle: 'italic', fontSize: '14px' }}>
            {t('quoteSnapshot.customerNote')}: {note}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function NoticeCard({ icon, title, description, testId }) {
  return (
    <Card data-testid={testId} sx={{ mb: '20px', borderRadius: theme.radius.xl, border: `2px solid ${theme.colors.border}` }}>
      <CardContent sx={{ textAlign: 'center', py: 3 }}>
        {icon}
        <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
        <Typography color="text.secondary">{description}</Typography>
      </CardContent>
    </Card>
  );
}

function AcceptanceCard({ quote, token, onResponded, t }) {
  const [responding, setResponding] = useState(false);
  const [note, setNote] = useState('');
  const [showNote, setShowNote] = useState(false);
  const [result, setResult] = useState(null);

  const respond = async (action) => {
    setResponding(true);
    setResult(null);
    try {
      await publicAxios.post(`/api/public/quote/${token}/respond/`, { action, note });
      setResult({ success: true, action });
      if (onResponded) onResponded(true);
    } catch (err) {
      const data = err.response?.data;
      setResult({ error: data?.error || t('Error'), code: data?.code });
      // The quote moved on (expired, superseded, already answered): show its real state.
      if (err.response?.status === 410 && onResponded) onResponded(false);
    } finally {
      setResponding(false);
    }
  };

  if (result?.success) {
    const accepted = result.action === 'accept';
    return (
      <ResultCard
        accepted={accepted}
        title={accepted ? t('quoteSnapshot.thankYouAccepted') : t('quoteSnapshot.declined')}
        description={accepted ? t('quoteSnapshot.thankYouAcceptedDescription') : t('quoteSnapshot.declinedDescription')}
        t={t}
      />
    );
  }

  return (
    <Card
      data-testid="public-quote-accept-card"
      sx={{ mb: '20px', borderRadius: theme.radius.xl, border: '2px solid', borderColor: 'warning.main' }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <CheckCircleOutlineIcon sx={{ color: 'warning.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('quoteSnapshot.acceptTitle')}</Typography>
        </Box>
        <Typography color="text.secondary" sx={{ mb: 2 }}>{t('quoteSnapshot.acceptIntro')}</Typography>
        {quote.valid_until && (
          <Typography sx={{ mb: 2, fontSize: '13px', color: theme.colors.textMuted, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ScheduleIcon sx={{ fontSize: 16 }} />
            {t('quoteSnapshot.validUntil', { date: formatDate(quote.valid_until) })}
          </Typography>
        )}
        {result?.error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>{result.error}</Alert>
        )}
        {!showNote ? (
          <Button variant="text" size="small" onClick={() => setShowNote(true)} sx={{ mb: 2 }}>
            {t('quoteSnapshot.addComment')}
          </Button>
        ) : (
          <TextField
            fullWidth
            multiline
            rows={2}
            size="small"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('quoteSnapshot.commentPlaceholder')}
            sx={{ mb: 2 }}
            inputProps={{ 'data-testid': 'public-quote-note' }}
          />
        )}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="success"
            data-testid="public-quote-accept-button"
            startIcon={responding ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
            onClick={() => respond('accept')}
            disabled={responding}
            sx={{ flex: 1, minWidth: 160, borderRadius: '10px', py: 1.5, fontWeight: 600 }}
          >
            {t('quoteSnapshot.accept')}
          </Button>
          <Button
            variant="outlined"
            color="error"
            data-testid="public-quote-decline-button"
            startIcon={responding ? <CircularProgress size={18} /> : <CancelIcon />}
            onClick={() => respond('decline')}
            disabled={responding}
            sx={{ flex: 1, minWidth: 160, borderRadius: '10px', py: 1.5, fontWeight: 600 }}
          >
            {t('quoteSnapshot.decline')}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

function QuoteStatusCard({ quote, token, onResponded, justResponded, t }) {
  switch (quote.status) {
    case 'accepted':
      return (
        <ResultCard
          accepted
          title={justResponded ? t('quoteSnapshot.thankYouAccepted') : t('quoteSnapshot.youAccepted')}
          description={justResponded ? t('quoteSnapshot.thankYouAcceptedDescription') : null}
          respondedAt={quote.responded_at}
          note={quote.customer_note}
          t={t}
        />
      );
    case 'declined':
      return (
        <ResultCard
          accepted={false}
          title={justResponded ? t('quoteSnapshot.declined') : t('quoteSnapshot.youDeclined')}
          description={justResponded ? t('quoteSnapshot.declinedDescription') : null}
          respondedAt={quote.responded_at}
          note={quote.customer_note}
          t={t}
        />
      );
    case 'expired':
      return (
        <NoticeCard
          testId="public-quote-expired"
          icon={<ScheduleIcon sx={{ fontSize: 40, color: theme.colors.textMuted, mb: 1 }} />}
          title={t('quoteSnapshot.expired')}
          description={t('quoteSnapshot.expiredDescription')}
        />
      );
    case 'withdrawn':
      return (
        <NoticeCard
          testId="public-quote-withdrawn"
          icon={<InfoOutlinedIcon sx={{ fontSize: 40, color: theme.colors.textMuted, mb: 1 }} />}
          title={t('quoteSnapshot.withdrawn')}
          description={t('quoteSnapshot.withdrawnDescription')}
        />
      );
    case 'superseded':
      return (
        <NoticeCard
          testId="public-quote-superseded"
          icon={<HistoryIcon sx={{ fontSize: 40, color: theme.colors.textMuted, mb: 1 }} />}
          title={t('quoteSnapshot.superseded')}
          description={t('quoteSnapshot.supersededDescription')}
        />
      );
    default:
      if (quote.acceptance_requested) {
        return <AcceptanceCard quote={quote} token={token} onResponded={onResponded} t={t} />;
      }
      return (
        <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: '20px', borderRadius: theme.radius.lg }}>
          {t('quoteSnapshot.infoOnly')}
        </Alert>
      );
  }
}

function PublicQuoteView() {
  const { token } = useParams();
  const { t } = useTranslation();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Set when the customer answered in this session: the refetched state is shown
  // with a thank-you wording instead of the plain "you accepted" record.
  const [justResponded, setJustResponded] = useState(false);

  const fetchQuote = useCallback(async () => {
    try {
      const response = await loadQuote(token);
      setQuote(response.data);
    } catch (err) {
      setError(err.response?.status === 404 ? t('quoteSnapshot.notFound') : t('quoteSnapshot.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  useEffect(() => {
    fetchQuote();
  }, [fetchQuote]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: theme.colors.background }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: theme.colors.background, p: 3 }}
        data-testid="public-quote-error"
      >
        <Typography variant="h5" color="error" sx={{ textAlign: 'center' }}>{error}</Typography>
      </Box>
    );
  }

  const isCostEstimate = quote.document_type === 'kustannusarvio';
  const title = isCostEstimate ? t('quoteSnapshot.pageTitleCostEstimate') : t('quoteSnapshot.pageTitle');

  return (
    <Box data-testid="public-quote-page" sx={{ minHeight: '100vh', bgcolor: theme.colors.background, display: 'flex', flexDirection: 'column' }}>
      <PublicHeader
        workshopName={quote.organization?.name}
        logoUrl={quote.organization?.logo_url}
        logoBgColor={quote.organization?.logo_bg_color}
        subtitle={title}
      />

      <Container maxWidth="sm" sx={{ py: 3, flex: 1 }}>
        <Paper elevation={0} sx={cardSx}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography component="h2" sx={{ fontSize: '22px', fontWeight: 700 }}>
                {title}
              </Typography>
              <Typography sx={{ fontSize: '13px', color: theme.colors.textMuted }}>
                {t('quoteSnapshot.sentAt')} {formatDate(quote.sent_at)}
              </Typography>
              {quote.valid_until && (
                <Typography sx={{ fontSize: '13px', color: theme.colors.textMuted }}>
                  {t('quoteSnapshot.validUntil', { date: formatDate(quote.valid_until) })}
                </Typography>
              )}
              {(quote.printed_bookings || []).map((slot, index) => (
                <Typography
                  key={`${slot.start}-${index}`}
                  data-testid="public-quote-booking"
                  sx={{ fontSize: '13px', color: theme.colors.textMuted, ...numFont }}
                >
                  {bookingText(slot, t)}
                </Typography>
              ))}
            </Box>
            {quote.customer_name && (
              <Typography sx={{ fontSize: '14px', fontWeight: 500, textAlign: 'right' }}>{quote.customer_name}</Typography>
            )}
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <DirectionsCarIcon sx={{ color: theme.colors.accent }} />
            <Box>
              <Typography sx={{ fontSize: '16px', fontWeight: 700, ...plateFont }}>
                {quote.vehicle_registration}
              </Typography>
              {quote.vehicle_make_model && (
                <Typography sx={{ fontSize: '13px', color: theme.colors.textMuted }}>{quote.vehicle_make_model}</Typography>
              )}
              {quote.work_number && (
                <Typography sx={{ fontSize: '12px', color: theme.colors.textLight }}>
                  {t('quoteSnapshot.workNumber')} {quote.work_number}
                </Typography>
              )}
            </Box>
          </Box>
        </Paper>

        <QuoteStatusCard
          quote={quote}
          token={token}
          justResponded={justResponded}
          onResponded={(responded) => {
            setJustResponded(Boolean(responded));
            fetchQuote();
          }}
          t={t}
        />

        {quote.lines?.length > 0 && <QuoteLinesCard quote={quote} t={t} />}

        <QuoteTotalsCard quote={quote} t={t} />

        {/* Customer notes printed on the PDF, frozen with this version. Images are in the PDF. */}
        {quote.printed_notes?.some((note) => note.text) && (
          <Paper elevation={0} sx={cardSx} data-testid="public-quote-notes">
            <Typography component="h3" sx={sectionTitleSx}>{t('quoteSnapshot.notes')}</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {quote.printed_notes
                .filter((note) => note.text)
                .map((note, index) => (
                  <Box key={`${note.created_at}-${index}`}>
                    <Typography sx={{ fontSize: '14px', lineHeight: 1.6, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                      {note.text}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: theme.colors.textMuted, mt: 0.5 }}>
                      {formatWorkshopDate(note.created_at)}
                    </Typography>
                  </Box>
                ))}
            </Box>
          </Paper>
        )}

        {quote.additional_text && (
          <Paper elevation={0} sx={cardSx}>
            <Typography component="h3" sx={sectionTitleSx}>{t('quoteSnapshot.additionalText')}</Typography>
            <Typography sx={{ fontSize: '14px', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{quote.additional_text}</Typography>
          </Paper>
        )}

        <Button
          variant="outlined"
          fullWidth
          href={quote.pdf_url}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<DownloadIcon />}
          data-testid="public-quote-pdf-link"
          sx={{ mb: '20px', borderRadius: '10px', py: 1.25, fontWeight: 600 }}
        >
          {isCostEstimate ? t('quoteSnapshot.downloadPdfCostEstimate') : t('quoteSnapshot.downloadPdf')}
        </Button>

        <WorkshopContactCard
          phone={quote.organization?.phone}
          address={quote.organization?.address}
        />
      </Container>

      <PublicFooter />
    </Box>
  );
}

export default PublicQuoteView;
